import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, ArrowLeft, Quote, MapPin, Star as StarIcon, ArrowRight, Calendar } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

function getYouTubeEmbed(url = "") {
    if (!url) return "";
    if (url.includes("/embed/")) return url;
    const m = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    return url;
}

export default function StoriesDetail() {
    const { slug } = useParams();
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/stories/${slug}`)
            .then(({ data }) => setStory(data))
            .catch((e) => setErr(e?.response?.status === 404 ? "Hikaye bulunamadı" : "Yüklenemedi"))
            .finally(() => setLoading(false));
    }, [slug]);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    const embed = useMemo(() => getYouTubeEmbed(story?.video_url || ""), [story]);

    useSeo({
        title: story?.seo?.meta_title || (story?.person?.name ? `${story.person.name} — Başarı Hikayesi` : "Başarı Hikayesi"),
        description: story?.seo?.meta_desc || tr(story?.featured_quote) || "",
        path: `/basari-hikayeleri/${slug}`,
        structuredData: story
            ? {
                  "@context": "https://schema.org",
                  "@type": "Review",
                  itemReviewed: { "@type": "Hotel", name: story.hotel?.name, address: story.hotel?.location },
                  reviewBody: tr(story.featured_quote),
                  author: { "@type": "Person", name: story.person?.name, jobTitle: story.person?.title },
                  publisher: { "@type": "Organization", name: "Otel Pro" },
              }
            : undefined,
    });

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stories-detail-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-4xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/basari-hikayeleri" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Başarı Hikayeleri
                    </Link>
                    {loading ? (
                        <div className="mt-6 text-white/70 text-sm">Yükleniyor...</div>
                    ) : err ? (
                        <h1 className="mt-3 text-3xl font-bold">{err}</h1>
                    ) : (
                        <>
                            <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">{story?.hotel?.name}</h1>
                            <div className="mt-3 flex items-center gap-5 text-sm text-white/80 font-data flex-wrap">
                                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-teal" /> {story?.hotel?.location}</span>
                                <span className="inline-flex items-center gap-1.5 text-gold"><StarIcon className="w-3.5 h-3.5" /> {story?.hotel?.star_level}★</span>
                                <span>{story?.person?.name} · {story?.person?.title}</span>
                                {story?.published_at && (
                                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(story.published_at).toLocaleDateString("tr-TR")}</span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {!loading && !err && story && (
                <>
                    <section className="max-w-4xl mx-auto px-6 -mt-10 relative z-10">
                        {embed ? (
                            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-2xl border border-slate-200" data-testid="story-video-embed">
                                <iframe
                                    title={story.hotel?.name}
                                    src={embed}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        ) : story.person?.photo ? (
                            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl border border-slate-200">
                                <img src={story.person.photo} alt={story.person.name} className="w-full h-full object-cover" />
                            </div>
                        ) : (
                            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl bg-deep-blue flex items-center justify-center text-white/30">
                                <Trophy className="w-16 h-16 text-gold/40" />
                            </div>
                        )}
                    </section>

                    <section className="max-w-3xl mx-auto px-6 py-12">
                        <div className="bg-white rounded-lg border-l-4 border-gold shadow-md p-7">
                            <Quote className="w-8 h-8 text-gold/40" />
                            <blockquote className="mt-3 text-xl sm:text-2xl text-dark-navy font-bold leading-relaxed">
                                "{tr(story.featured_quote)}"
                            </blockquote>
                            <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-deep-blue/70">
                                — <strong className="text-dark-navy">{story.person?.name}</strong>, {story.person?.title}
                            </div>
                        </div>

                        <article
                            className="mt-10 prose prose-slate max-w-none prose-headings:text-dark-navy prose-a:text-teal-700 prose-strong:text-dark-navy prose-p:text-deep-blue/85 prose-p:leading-relaxed"
                            data-testid="story-article"
                            dangerouslySetInnerHTML={{ __html: tr(story.content) }}
                        />
                    </section>

                    {story.related_stories?.length > 0 && (
                        <section className="bg-white py-12 border-t border-slate-200">
                            <div className="max-w-5xl mx-auto px-6">
                                <h2 className="text-lg font-bold text-dark-navy mb-5">Diğer Başarı Hikayeleri</h2>
                                <div className="grid sm:grid-cols-3 gap-4" data-testid="story-related">
                                    {story.related_stories.map((r) => (
                                        <Link
                                            key={r.id}
                                            to={`/basari-hikayeleri/${r.seo?.slug || r.id}`}
                                            className="lift-card group bg-light-bg rounded-lg p-5 hover:bg-white border border-transparent hover:border-teal/40"
                                        >
                                            <Quote className="w-4 h-4 text-teal" />
                                            <p className="mt-2 text-sm text-deep-blue/85 line-clamp-3">{tr(r.featured_quote)}</p>
                                            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-deep-blue/70">
                                                <strong className="text-dark-navy">{r.person?.name}</strong> · {r.hotel?.name}
                                            </div>
                                            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">
                                                Oku <ArrowRight className="w-3 h-3" />
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
